import ListItem from '@mui/material/ListItem';
import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';

import { NavigatableLink } from '@/shared/components/ui/navigatable-link/navigatable-link';
import { Tooltip } from '@/shared/components/ui/tooltip/tooltip';
import { useHasPermission } from '@/shared/features/permissions/hooks/use-permission-check/use-permission-check';
import {
  NavigationItemButton,
  NavigationItemIcon,
  NavigationItemName,
} from '@/shared/features/sidebar/components/navigation-item/navigation-item.styled';
import { TNavigationItemProps } from '@/shared/features/sidebar/components/navigation-item/navigation-item.types';
import { NavigationItemType } from '@/shared/features/sidebar/sidebar.types';

export const NavigationItem: React.FC<
  TNavigationItemProps & NavigationItemType
> = ({
  name,
  icon,
  link,
  open,
  prefetchApi,
  disabled,
  onClick,
  permission,
  clicked,
}) => {
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  const hasPermission = useHasPermission(permission);

  const isActive = () => {
    if (!link) {
      return false;
    }

    const pathname = location.pathname;

    if (link === '/') {
      return (
        pathname === '/' ||
        Object.values(params).some(
          (param) => param && pathname.startsWith(`/${param}`),
        )
      );
    }

    if (typeof link === 'string') {
      return pathname?.startsWith(link);
    }

    return link.some((link) => pathname?.startsWith(link));
  };

  const getLink = () => {
    if (!link) {
      return '';
    }

    if (typeof link === 'string') {
      return link;
    }

    return link[0];
  };

  const onMouseEnter = () => {
    if (!prefetchApi) {
      return;
    }

    prefetchApi();
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
      return;
    }

    if (!link) {
      return;
    }

    navigate(getLink());
  };

  const renderContent = () => {
    return (
      <ListItem disablePadding>
        <Tooltip
          title={name}
          placement="right"
          arrow
          wrapper="fullWidth"
          disableHoverListener={open}
        >
          <NavigationItemButton
            disabled={disabled}
            onMouseEnter={onMouseEnter}
            onClick={handleClick}
            $active={isActive()}
            $clicked={clicked}
          >
            <NavigationItemIcon>{icon}</NavigationItemIcon>
            <NavigationItemName $open={open} primary={name} />
          </NavigationItemButton>
        </Tooltip>
      </ListItem>
    );
  };

  if (!hasPermission) {
    return null;
  }

  if (!link) {
    return renderContent();
  }

  return <NavigatableLink to={getLink()}>{renderContent()}</NavigatableLink>;
};
